import React, { useState, useEffect } from 'react';
import { API_BASE_URL, API_URL, resolveUploadUrl } from "../config";
import { 
  FaUser, FaEnvelope, FaPhone, FaBuilding, 
  FaSave, FaEdit, FaTimes, FaShieldAlt,
  FaFileAlt, FaCalendarAlt, FaCheckCircle, FaExclamationTriangle,
  FaTruck, FaTrailer, FaUserTie, FaUniversity, FaUsers,
  FaIdCard, FaClock, FaUpload, FaDownload, FaEye,
  FaSpinner, FaInfoCircle, FaPlus, FaLock, FaTrash, FaMapMarkerAlt
} from 'react-icons/fa';
import SubbieLayout from '../layouts/SubbieLayout';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import LocationPicker from '../components/LocationPicker';
import OperatingAreasInput from '../components/OperatingAreasInput';
import EditInsuranceModal from '../components/EditInsuranceModal';
import UploadDocumentModal from '../components/UploadDocumentModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import BankDetailsConfirmModal from '../components/BankDetailsConfirmModal';
import { toast } from 'react-toastify';
import CompanyUsersCard from '../components/CompanyUsersCard';

function SubbieProfile({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subbie, setSubbie] = useState(null);
  const [subbieId, setSubbieId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Modal states
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [insuranceType, setInsuranceType] = useState('git');
  const [insuranceData, setInsuranceData] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { isLoaded } = useGoogleMaps();

  // Document types for upload
  const documentTypes = [
    { key: 'cipc_document', display_name: 'CIPC Registration Certificate', required: true },
    { key: 'git_insurance', display_name: 'GIT Insurance Certificate', required: true },
    { key: 'public_liability', display_name: 'Public Liability Insurance', required: true },
    { key: 'bank_confirmation', display_name: 'Bank Confirmation Letter', required: true },
    { key: 'tax_clearance', display_name: 'Tax Clearance Certificate', required: true },
    { key: 'bbbee_certificate', display_name: 'B-BBEE Certificate', required: false },
    { key: 'id_document', display_name: 'ID Document', required: true },
    { key: 'liveness_photo', display_name: 'Liveness Photo', required: true }
  ];

  useEffect(() => {
    const getSubbieId = async () => {
      try {
        let savedId = localStorage.getItem('subbieId');
        if (savedId) {
          setSubbieId(savedId);
          fetchData(savedId);
          return;
        }

        const response = await fetch(`/subcontractors?user_id=${user.id}`);
        const data = await response.json();
        if (data.success && data.subcontractors && data.subcontractors.length > 0) {
          const id = data.subcontractors[0].id;
          setSubbieId(id);
          localStorage.setItem('subbieId', id);
          fetchData(id);
        } else {
          setError('No subcontractor profile found');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching subbie ID:', err);
        setError('Failed to load profile');
        setLoading(false);
      }
    };

    if (user.id) {
      getSubbieId();
    } else {
      setError('User not logged in');
      setLoading(false);
    }
  }, []);

  const fetchData = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/subcontractors/${id}`);
      const data = await response.json();
      if (data.success) {
        setSubbie(data.subcontractor);
        setFormData(data.subcontractor);
      } else {
        setError(data.message || 'Failed to load profile');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      const response = await fetch(`/subcontractors/${subbieId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: 'Profile updated successfully!' });
        setEditMode(false);
        setSubbie(formData);
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (formData.contact_person) {
          storedUser.full_name = formData.contact_person;
          localStorage.setItem('user', JSON.stringify(storedUser));
        }
        toast.success('✅ Profile updated successfully!');
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update profile' });
        toast.error(data.error || 'Failed to update profile');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setMessage({ type: 'error', text: 'Failed to update profile' });
      toast.error('Failed to update profile');
    } finally {
      setProcessing(false);
    }
  };

  // ============================================
  // ✅ EXTRA LOCATIONS (depots — main + extra pins)
  // ============================================
  const extraLocations = Array.isArray(formData.locations) ? formData.locations : [];

  const addLocation = () => {
    setFormData(prev => ({
      ...prev,
      locations: [...(Array.isArray(prev.locations) ? prev.locations : []), { label: '', address: '', lat: null, lng: null }]
    }));
  };

  const updateLocation = (index, patch) => {
    setFormData(prev => {
      const locs = [...(Array.isArray(prev.locations) ? prev.locations : [])];
      locs[index] = { ...locs[index], ...patch };
      return { ...prev, locations: locs };
    });
  };

  const removeLocation = (index) => {
    setFormData(prev => ({
      ...prev,
      locations: (Array.isArray(prev.locations) ? prev.locations : []).filter((_, i) => i !== index)
    }));
  };

  const geocodeLocation = (index) => {
    if (!window.google?.maps) { toast.error('Google Maps not ready yet'); return; }
    const item = extraLocations[index];
    if (!item?.address || item.address.trim().length < 3) { toast.error('Enter an address first'); return; }
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: item.address }, (results, status) => {
      if (status === 'OK' && results && results[0]?.geometry) {
        const lat = results[0].geometry.location.lat();
        const lng = results[0].geometry.location.lng();
        updateLocation(index, { lat, lng, address: results[0].formatted_address });
        toast.success('📍 Location pinned!');
      } else {
        toast.error('Could not locate that address — check the spelling.');
      }
    });
  };

  // ✅ COMPANY LOGO / PROFILE PICTURE UPLOAD
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const logoForm = new FormData();
      logoForm.append('image', file);
      const upRes = await fetch(`${API_BASE_URL}/api/uploads`, { method: 'POST', body: logoForm });
      const upData = await upRes.json();
      if (!upData.success) throw new Error(upData.error || 'Logo upload failed');

      // Store the relative path (env-agnostic); resolveUploadUrl adds the origin at render.
      const relativeUrl = upData.url || upData.file_url || upData.path || null;
      if (!relativeUrl) throw new Error('Upload returned no URL');

      const saveRes = await fetch(`/subcontractors/${subbieId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_logo_url: relativeUrl })
      });
      const saveData = await saveRes.json();
      if (!saveData.success) throw new Error(saveData.error || 'Failed to save logo');

      toast.success('✅ Logo updated!');
      fetchData(subbieId);
    } catch (err) {
      console.error('Logo upload error:', err);
      toast.error(err.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  // ============================================
  // ✅ INSURANCE FUNCTIONS
  // ============================================
  const handleEditInsurance = (type) => {
    const dataMap = {
      'git': {
        provider: subbie.git_insurance_provider || '',
        policy_number: subbie.git_policy_number || '',
        expiry: subbie.git_insurance_expiry || '',
        cover_amount: subbie.git_cover_amount || ''
      },
      'pl': {
        provider: subbie.public_liability_provider || '',
        policy_number: subbie.public_liability_policy_number || '',
        expiry: subbie.public_liability_expiry || '',
        cover_amount: subbie.public_liability_cover_amount || ''
      },
      'tax': {
        provider: '',
        policy_number: '',
        expiry: subbie.tax_clearance_expiry || '',
        cover_amount: ''
      }
    };
    setInsuranceType(type);
    setInsuranceData(dataMap[type]);
    setShowInsuranceModal(true);
  };

  const handleSaveInsurance = async (data) => {
    const endpoints = {
      'git': `/api/subcontractors/${subbieId}/insurance/git`,
      'pl': `/api/subcontractors/${subbieId}/insurance/pl`,
      'tax': `/api/subcontractors/${subbieId}/insurance/tax`
    };

    const payload = {
      ...data,
      expiry: data.expiry
    };

    const response = await fetch(endpoints[insuranceType], {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const result = await response.json();
    if (result.success) {
      toast.success('✅ Insurance details updated! Waiting for admin approval.');
      fetchData(subbieId);
    } else {
      throw new Error(result.error || 'Failed to update insurance');
    }
  };

  const handleUploadInsurance = async (type, file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('certificate', file);
      formData.append('type', type);

      const response = await fetch(`/subcontractors/${subbieId}/insurance/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        toast.success('✅ Insurance certificate uploaded successfully!');
        fetchData(subbieId);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.message || 'Failed to upload certificate');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  // ============================================
  // ✅ DOCUMENT FUNCTIONS
  // ============================================
  const handleUploadDocument = async (docType, file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('type', docType);

      const response = await fetch(`/subcontractors/${subbieId}/documents`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        toast.success('✅ Document uploaded successfully!');
        fetchData(subbieId);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.message || 'Failed to upload document');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  // ============================================
  // ✅ EXPIRY CHECK
  // ============================================
  const checkExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'EXPIRED', color: 'text-red-600 bg-red-50', icon: <FaExclamationTriangle className="text-red-500" />, days: daysUntilExpiry };
    } else if (daysUntilExpiry < 30) {
      return { status: 'expiring', label: `${daysUntilExpiry} days remaining`, color: 'text-yellow-600 bg-yellow-50', icon: <FaClock className="text-yellow-500" />, days: daysUntilExpiry };
    } else {
      return { status: 'valid', label: 'Valid', color: 'text-green-600 bg-green-50', icon: <FaCheckCircle className="text-green-500" />, days: daysUntilExpiry };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric'
    });
  };

  const getDocumentUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return window.getServerUrl(url);
  };

  const viewDocument = (url) => {
    if (!url) {
      toast.warning('No document uploaded');
      return;
    }
    window.open(getDocumentUrl(url), '_blank');
  };

  const downloadDocument = (url, filename) => {
    if (!url) {
      toast.warning('No document uploaded');
      return;
    }
    const link = document.createElement('a');
    link.href = getDocumentUrl(url);
    link.download = filename || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get documents list
  const getDocuments = () => {
    if (!subbie) return [];
    const docMap = {
      'cipc_document_url': { label: 'CIPC Registration', key: 'cipc_document' },
      'cipc_annual_return_url': { label: 'CIPC Annual Return', key: 'cipc_annual_return' },
      'shareholder_register_url': { label: 'Shareholder Register', key: 'shareholder_register' },
      'git_insurance_url': { label: 'GIT Insurance', key: 'git_insurance' },
      'public_liability_url': { label: 'Public Liability', key: 'public_liability' },
      'operator_card_url': { label: 'Operator Card', key: 'operator_card' },
      'bank_confirmation_url': { label: 'Bank Confirmation', key: 'bank_confirmation' },
      'bank_statement_url': { label: 'Bank Statement', key: 'bank_statement' },
      'tax_clearance_certificate_url': { label: 'Tax Clearance', key: 'tax_clearance' },
      'bbbee_certificate_url': { label: 'B-BBEE Certificate', key: 'bbbee_certificate' },
      'id_document_url': { label: 'ID Document', key: 'id_document' },
      'liveness_photo_url': { label: 'Liveness Photo', key: 'liveness_photo' }
    };

    const docs = [];
    for (const [key, info] of Object.entries(docMap)) {
      if (subbie[key]) {
        docs.push({
          label: info.label,
          url: subbie[key],
          key: info.key
        });
      }
    }
    return docs;
  };

  // Get expiring items
  const getExpiringItems = () => {
    if (!subbie) return [];
    const items = [];
    
    const gitExpiry = checkExpiry(subbie.git_insurance_expiry);
    if (gitExpiry && gitExpiry.status !== 'valid') {
      items.push({ type: 'GIT Insurance', expiry: subbie.git_insurance_expiry, status: gitExpiry });
    }
    
    const plExpiry = checkExpiry(subbie.public_liability_expiry);
    if (plExpiry && plExpiry.status !== 'valid') {
      items.push({ type: 'Public Liability', expiry: subbie.public_liability_expiry, status: plExpiry });
    }
    
    const taxExpiry = checkExpiry(subbie.tax_clearance_expiry);
    if (taxExpiry && taxExpiry.status !== 'valid') {
      items.push({ type: 'Tax Clearance', expiry: subbie.tax_clearance_expiry, status: taxExpiry });
    }
    
    return items;
  };

  if (loading) {
    return (
      <SubbieLayout activeTab="profile" onLogout={onLogout} user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
            <p className="text-gray-500">Loading profile...</p>
          </div>
        </div>
      </SubbieLayout>
    );
  }

  if (error || !subbie) {
    return (
      <SubbieLayout activeTab="profile" onLogout={onLogout} user={user}>
        <div className="bg-white rounded-xl p-8 text-center border border-gray-200 max-w-md mx-auto">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-red-600 mb-2">Error Loading Profile</h2>
          <p className="text-gray-500">{error || 'Profile not found'}</p>
          <button 
            onClick={() => fetchData(subbieId)}
            className="mt-4 bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors"
          >
            Retry
          </button>
        </div>
      </SubbieLayout>
    );
  }

  const documents = getDocuments();
  const expiringItems = getExpiringItems();
  const gitExpiry = checkExpiry(subbie.git_insurance_expiry);
  const plExpiry = checkExpiry(subbie.public_liability_expiry);
  const taxExpiry = checkExpiry(subbie.tax_clearance_expiry);

  // Tier-aware document list (invited subbies skip tax clearance / ID / liveness)
  const isInvited = subbie.onboarding_source === 'invited' || (!subbie.application_submitted_at && subbie.onboarding_source !== 'self');
  const profileDocumentTypes = isInvited
    ? documentTypes.filter((d) => !['tax_clearance', 'id_document', 'liveness_photo'].includes(d.key))
    : documentTypes;

  // Completeness for the "Complete Your Profile" banner (mirrors the client flow)
  const profileMissing = (() => {
    const m = [];
    if (!subbie?.company_name) m.push('Company Name');
    if (!subbie?.registration_number) m.push('Company Reg');
    if (!subbie?.contact_person) m.push('Contact');
    if (!subbie?.contact_phone) m.push('Phone');
    if (!subbie?.contact_email) m.push('Email');
    if (!subbie?.bank_name || !subbie?.bank_account_number || !subbie?.bank_branch_code) m.push('Banking Details');
    return m;
  })();

  return (
    <SubbieLayout activeTab="profile" onLogout={onLogout} user={user}>
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy">👤 My Profile</h1>
            <p className="text-gray-500 text-sm">View and manage your profile information</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowPasswordModal(true)}
              style={{
                padding: '8px 16px',
                backgroundColor: '#2563EB',
                color: 'white',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1D4ED8'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
            >
              <FaLock size={14} /> Change Password
            </button>
            <button
              onClick={() => setEditMode(!editMode)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm hover:border-green hover:text-green transition-colors"
            >
              {editMode ? <FaTimes size={14} /> : <FaEdit size={14} />}
              {editMode ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>
        </div>

        {/* ✅ COMPLETE YOUR PROFILE BANNER */}
        {profileMissing.length > 0 && (
          <div className="rounded-xl p-6 mb-6 border-2" style={{ backgroundColor: '#EBF5FF', borderColor: '#3B82F6' }}>
            <div className="flex items-start gap-4">
              <div className="text-3xl">📋</div>
              <div className="flex-1">
                <h3 className="font-bold text-lg" style={{ color: '#1E40AF' }}>Complete Your Profile</h3>
                <p style={{ color: '#1E3A8A' }}>
                  Please update your profile with:{' '}
                  <strong>{profileMissing.join(', ')}</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ✅ COMPANY LOGO / PROFILE PICTURE */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 flex items-center gap-5">
          <div className="w-20 h-20 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden" style={{ border: '1px solid #E8ECEF' }}>
            {subbie.company_logo_url ? (
              <img src={resolveUploadUrl(subbie.company_logo_url)} alt="Company logo" className="w-full h-full object-contain" />
            ) : (
              <FaBuilding className="text-3xl text-gray-300" />
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-navy mb-1">Company Logo</h3>
            <p className="text-sm text-gray-500 mb-3">Upload your company logo or profile picture (JPEG, PNG).</p>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white cursor-pointer hover:opacity-90 transition-opacity" style={{ backgroundColor: '#00A86B' }}>
              <FaUpload size={14} />
              {subbie.company_logo_url ? 'Change Logo' : 'Upload Logo'}
              <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
            </label>
          </div>
        </div>

        {message && (
          <div className={`p-4 rounded-lg mb-4 ${message.type === 'success' ? 'bg-green-50 text-green-600 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        {/* ✅ EXPIRING SOON SECTION */}
        {expiringItems.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-red-700 flex items-center gap-2 mb-2">
              <FaExclamationTriangle /> Items Requiring Attention
            </h3>
            <div className="space-y-1">
              {expiringItems.map((item, index) => (
                <p key={index} className="text-sm text-red-600">
                  ⚠️ {item.type} {item.status.status === 'expired' ? 'has EXPIRED' : `expires in ${item.status.days} days`} ({formatDate(item.expiry)})
                </p>
              ))}
            </div>
          </div>
        )}

        {/* ✅ PHASE 2 — the primary contact maintains their own company's logins */}
        {!!subbie?.user_id && subbie.user_id === user?.id && (
          <div className="mb-6">
            <CompanyUsersCard
              companyType="subcontractor"
              companyId={subbie.id}
              companyName={subbie.company_name}
              className="rounded-xl border p-6"
            />
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-6" autoComplete="off">
          {/* Company Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
              <FaBuilding className="text-green" /> Company Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.company_name || ''}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700 font-medium">{subbie.company_name}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trading Name</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.trading_name || ''}
                    onChange={(e) => setFormData({ ...formData, trading_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.trading_name || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.registration_number || ''}
                    onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.registration_number || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.vat_number || ''}
                    onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.vat_number || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Years in Business</label>
                {editMode ? (
                  <input
                    type="number"
                    value={formData.years_in_business || ''}
                    onChange={(e) => setFormData({ ...formData, years_in_business: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.years_in_business || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operating Areas</label>
                {editMode ? (
                  <OperatingAreasInput
                    value={(formData.operating_areas || '').split(',').map((s) => s.trim()).filter(Boolean)}
                    onChange={(areas) => setFormData({ ...formData, operating_areas: areas.join(', ') })}
                  />
                ) : (
                  <div className="py-1">
                    {String(subbie.operating_areas || '').split(',').map((a) => a.trim()).filter(Boolean).length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {String(subbie.operating_areas).split(',').map((a, i) => (
                          <span key={i} className="px-2.5 py-0.5 rounded-full text-xs bg-green-100 text-green-700 font-medium">
                            {a.trim()}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="py-2 text-gray-700">N/A</p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Phone</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.company_phone || ''}
                    onChange={(e) => setFormData({ ...formData, company_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.company_phone || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Website</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.company_website || ''}
                    onChange={(e) => setFormData({ ...formData, company_website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.company_website || 'N/A'}</p>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                {editMode ? (
                  isLoaded ? (
                    <LocationPicker
                      label="Physical Address"
                      value={formData.company_address || ''}
                      onChange={(addr) => setFormData({ ...formData, company_address: addr })}
                      onCoordinatesChange={(c) => setFormData({ ...formData, company_lat: c.lat, company_lng: c.lng, company_address: c.address || formData.company_address })}
                      initialCoordinates={(formData.company_lat && formData.company_lng) ? { lat: Number(formData.company_lat), lng: Number(formData.company_lng) } : null}
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData.company_address || ''}
                      onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                      placeholder="Loading Google Maps..."
                    />
                  )
                ) : (
                  <div>
                    <p className="py-2 text-gray-700">{subbie.company_address || 'N/A'}</p>
                    {subbie.company_lat && subbie.company_lng && isLoaded && (
                      <div className="mt-2 rounded-lg overflow-hidden border" style={{ borderColor: '#E8ECEF', height: '180px' }}>
                        <GoogleMap
                          mapContainerStyle={{ width: '100%', height: '100%' }}
                          center={{ lat: Number(subbie.company_lat), lng: Number(subbie.company_lng) }}
                          zoom={14}
                          options={{ mapTypeControl: false, streetViewControl: false }}
                        >
                          <Marker position={{ lat: Number(subbie.company_lat), lng: Number(subbie.company_lng) }} />
                        </GoogleMap>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Postal Address</label>
                {editMode ? (
                  <textarea
                    value={formData.postal_address || ''}
                    onChange={(e) => setFormData({ ...formData, postal_address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                    rows="2"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.postal_address || 'N/A'}</p>
                )}
              </div>
            </div>
          </div>

          {/* ✅ EXTRA LOCATIONS (DEPOTS) */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-navy flex items-center gap-2">
              <FaMapMarkerAlt className="text-green" /> Additional Locations
            </h3>
            {editMode && (
              <button
                type="button"
                onClick={addLocation}
                className="flex items-center gap-2 px-3 py-1.5 bg-green text-white rounded-lg text-xs font-medium"
              >
                <FaPlus size={12} /> Add Extra Location
              </button>
            )}
          </div>

          {extraLocations.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">
              No extra locations. Use "Add Extra Location" to list depots (e.g. Cape Town, Boksburg, Durban).
            </p>
          ) : (
            <div className="space-y-3">
              {extraLocations.map((loc, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-3">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto_auto] gap-2">
                    <input
                      type="text"
                      value={loc.label || ''}
                      onChange={(e) => updateLocation(index, { label: e.target.value })}
                      placeholder="Label (e.g. Cape Town Depot)"
                      disabled={!editMode}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green"
                    />
                    <input
                      type="text"
                      value={loc.address || ''}
                      onChange={(e) => updateLocation(index, { address: e.target.value })}
                      placeholder="Physical address"
                      disabled={!editMode}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green"
                    />
                    {editMode && (
                      <button
                        type="button"
                        onClick={() => geocodeLocation(index)}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium"
                        title="Find pin on map"
                      >
                        📍 Pin
                      </button>
                    )}
                    {editMode && (
                      <button
                        type="button"
                        onClick={() => removeLocation(index)}
                        className="px-3 py-2 bg-red-50 text-red-600 rounded-lg text-xs"
                        title="Remove location"
                      >
                        <FaTrash size={12} />
                      </button>
                    )}
                  </div>
                  {loc.lat && loc.lng && (
                    <p className="text-xs text-gray-400 mt-2">
                      📍 {Number(loc.lat).toFixed(6)}, {Number(loc.lng).toFixed(6)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
              <FaUser className="text-green" /> Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.contact_person || ''}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700 font-medium">{subbie.contact_person}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.position || ''}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.position || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.contact_phone || ''}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.contact_phone}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                {editMode ? (
                  <input
                    type="email"
                    value={formData.contact_email || ''}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.contact_email}</p>
                )}
              </div>
            </div>
          </div>

          {/* ✅ INSURANCE DETAILS - UPDATED WITH EDIT BUTTONS */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
              <FaShieldAlt className="text-green" /> Insurance Details
              <span className="text-xs text-gray-400 ml-auto">Updates require admin approval</span>
            </h3>
            
            {/* GIT Insurance */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-medium text-navy flex items-center gap-2">
                  🚛 GIT Insurance (Goods in Transit)
                  {gitExpiry && (
                    <span className={`text-xs px-2 py-1 rounded-full ${gitExpiry.color}`}>
                      {gitExpiry.icon} {gitExpiry.label}
                    </span>
                  )}
                </h4>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleEditInsurance('git')}
                    className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 transition-colors flex items-center gap-1"
                  >
                    <FaEdit size={10} /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setInsuranceType('git');
                      setShowUploadModal(true);
                    }}
                    className="px-3 py-1 bg-green text-white rounded-lg text-xs hover:bg-green transition-colors flex items-center gap-1"
                  >
                    <FaUpload size={10} /> Upload
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">Provider:</span> <span className="font-medium">{subbie.git_insurance_provider || 'N/A'}</span></div>
                <div><span className="text-gray-500">Policy #:</span> <span className="font-medium">{subbie.git_policy_number || 'N/A'}</span></div>
                <div><span className="text-gray-500">Cover Amount:</span> <span className="font-medium">{subbie.git_cover_amount ? `R${subbie.git_cover_amount.toLocaleString()}` : 'N/A'}</span></div>
                <div><span className="text-gray-500">Expiry:</span> <span className="font-medium">{formatDate(subbie.git_insurance_expiry)}</span></div>
              </div>
              {subbie.git_insurance_url && (
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => viewDocument(subbie.git_insurance_url)}
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <FaEye size={10} /> View Certificate
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadDocument(subbie.git_insurance_url, 'git_insurance.pdf')}
                    className="text-xs text-green-600 hover:underline flex items-center gap-1"
                  >
                    <FaDownload size={10} /> Download
                  </button>
                </div>
              )}
            </div>

            {/* Public Liability */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-medium text-navy flex items-center gap-2">
                  🏢 Public Liability Insurance
                  {plExpiry && (
                    <span className={`text-xs px-2 py-1 rounded-full ${plExpiry.color}`}>
                      {plExpiry.icon} {plExpiry.label}
                    </span>
                  )}
                </h4>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleEditInsurance('pl')}
                    className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 transition-colors flex items-center gap-1"
                  >
                    <FaEdit size={10} /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setInsuranceType('pl');
                      setShowUploadModal(true);
                    }}
                    className="px-3 py-1 bg-green text-white rounded-lg text-xs hover:bg-green transition-colors flex items-center gap-1"
                  >
                    <FaUpload size={10} /> Upload
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">Provider:</span> <span className="font-medium">{subbie.public_liability_provider || 'N/A'}</span></div>
                <div><span className="text-gray-500">Policy #:</span> <span className="font-medium">{subbie.public_liability_policy_number || 'N/A'}</span></div>
                <div><span className="text-gray-500">Cover Amount:</span> <span className="font-medium">{subbie.public_liability_cover_amount ? `R${subbie.public_liability_cover_amount.toLocaleString()}` : 'N/A'}</span></div>
                <div><span className="text-gray-500">Expiry:</span> <span className="font-medium">{formatDate(subbie.public_liability_expiry)}</span></div>
              </div>
              {subbie.public_liability_url && (
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => viewDocument(subbie.public_liability_url)}
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <FaEye size={10} /> View Certificate
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadDocument(subbie.public_liability_url, 'public_liability.pdf')}
                    className="text-xs text-green-600 hover:underline flex items-center gap-1"
                  >
                    <FaDownload size={10} /> Download
                  </button>
                </div>
              )}
            </div>

            {/* Tax Clearance */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-medium text-navy flex items-center gap-2">
                  📄 Tax Clearance Certificate
                  {taxExpiry && (
                    <span className={`text-xs px-2 py-1 rounded-full ${taxExpiry.color}`}>
                      {taxExpiry.icon} {taxExpiry.label}
                    </span>
                  )}
                </h4>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleEditInsurance('tax')}
                    className="px-3 py-1 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700 transition-colors flex items-center gap-1"
                  >
                    <FaEdit size={10} /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setInsuranceType('tax');
                      setShowUploadModal(true);
                    }}
                    className="px-3 py-1 bg-green text-white rounded-lg text-xs hover:bg-green transition-colors flex items-center gap-1"
                  >
                    <FaUpload size={10} /> Upload
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">Expiry:</span> <span className="font-medium">{formatDate(subbie.tax_clearance_expiry)}</span></div>
              </div>
              {subbie.tax_clearance_certificate_url && (
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={() => viewDocument(subbie.tax_clearance_certificate_url)}
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <FaEye size={10} /> View Certificate
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadDocument(subbie.tax_clearance_certificate_url, 'tax_clearance.pdf')}
                    className="text-xs text-green-600 hover:underline flex items-center gap-1"
                  >
                    <FaDownload size={10} /> Download
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Banking Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-navy flex items-center gap-2">
                <FaUniversity className="text-green" /> Banking Details
              </h3>
              <button
                onClick={() => setShowBankModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-green text-white rounded-lg text-xs font-medium hover:bg-green"
              >
                <FaShieldAlt size={12} /> Confirm / Update Bank Details
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.bank_name || ''}
                    onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.bank_name || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.bank_branch || ''}
                    onChange={(e) => setFormData({ ...formData, bank_branch: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.bank_branch || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.bank_account_number || ''}
                    onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.bank_account_number || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.bank_account_type || ''}
                    onChange={(e) => setFormData({ ...formData, bank_account_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.bank_account_type || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Branch Code</label>
                {editMode ? (
                  <input
                    type="text"
                    value={formData.bank_branch_code || ''}
                    onChange={(e) => setFormData({ ...formData, bank_branch_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                ) : (
                  <p className="py-2 text-gray-700">{subbie.bank_branch_code || 'N/A'}</p>
                )}
              </div>
            </div>
          </div>

          {/* References */}
          {(subbie.reference_1_name || subbie.reference_2_name) && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
                <FaUsers className="text-green" /> References
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {subbie.reference_1_name && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-navy mb-2">Reference 1</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-gray-500">Name:</span> {subbie.reference_1_name}</p>
                      <p><span className="text-gray-500">Company:</span> {subbie.reference_1_company || 'N/A'}</p>
                      <p><span className="text-gray-500">Phone:</span> {subbie.reference_1_phone || 'N/A'}</p>
                      <p><span className="text-gray-500">Email:</span> {subbie.reference_1_email || 'N/A'}</p>
                    </div>
                  </div>
                )}
                {subbie.reference_2_name && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-navy mb-2">Reference 2</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-gray-500">Name:</span> {subbie.reference_2_name}</p>
                      <p><span className="text-gray-500">Company:</span> {subbie.reference_2_company || 'N/A'}</p>
                      <p><span className="text-gray-500">Phone:</span> {subbie.reference_2_phone || 'N/A'}</p>
                      <p><span className="text-gray-500">Email:</span> {subbie.reference_2_email || 'N/A'}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Edit Mode Actions */}
          {editMode && (
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditMode(false)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing}
                className="px-4 py-2 bg-navy text-white rounded-lg text-sm hover:bg-green hover:text-navy transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {processing ? <FaSpinner className="animate-spin" /> : <FaSave size={14} />}
                {processing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </form>

        {/* Documents Section */}
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-navy flex items-center gap-2">
              <FaFileAlt className="text-green" /> Documents ({documents.length})
            </h3>
            <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              className="px-3 py-1.5 bg-green text-white rounded-lg text-xs font-medium flex items-center gap-2"
            >
              <FaUpload size={12} /> Upload Document
            </button>
          </div>
          {documents.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">
              No documents uploaded yet. Click "Upload Document" to add your compliance documents
              (e.g. Bank Confirmation Letter, CIPC, B-BBEE Certificate, Insurance).
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {documents.map((doc) => (
                <div key={doc.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FaFileAlt className="text-gray-400" />
                    <span className="text-sm text-gray-700">{doc.label}</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => viewDocument(doc.url)}
                      className="text-blue-600 hover:text-blue-800 text-xs"
                    >
                      <FaEye size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadDocument(doc.url, `${doc.label}.pdf`)}
                      className="text-green-600 hover:text-green-800 text-xs"
                    >
                      <FaDownload size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Account Info */}
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="font-semibold text-navy mb-4 flex items-center gap-2">
            <FaCheckCircle className="text-green" /> Account Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <p className="py-2">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${subbie.verification_status === 'approved' || subbie.verification_status === 'active' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                  {subbie.verification_status || 'pending'}
                </span>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
              <p className="py-2 text-gray-700">
                {subbie.created_at ? new Date(subbie.created_at).toLocaleDateString('en-ZA', { 
                  day: '2-digit', 
                  month: 'long', 
                  year: 'numeric' 
                }) : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================
          MODALS
      ============================================ */}
      
      {/* Edit Insurance Modal */}
      <EditInsuranceModal
        isOpen={showInsuranceModal}
        onClose={() => setShowInsuranceModal(false)}
        onSave={handleSaveInsurance}
        title="Insurance"
        type={insuranceType}
        data={insuranceData}
        subbieId={subbieId}
        isSubbie={true}
      />

      {/* Upload Document Modal */}
      <UploadDocumentModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUploadDocument}
        subbieId={subbieId}
        documentTypes={profileDocumentTypes}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        userId={user.id}
      />

      {/* Bank Details Confirmation Modal */}
      <BankDetailsConfirmModal
        open={showBankModal}
        onClose={() => setShowBankModal(false)}
        onDone={() => fetchData(subbieId)}
      />
    </SubbieLayout>
  );
}

export default SubbieProfile;
